import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Input,
  Button,
  Field,
  Spinner,
  Text,
  Tooltip
} from "@fluentui/react-components";
import { DialogBox } from "#root/components/DialogBox";
import { AzureMap, AzureMapsProvider, IAzureMapOptions } from 'react-azure-maps';
import { AuthenticationType } from 'azure-maps-control';
import * as atlas from 'azure-maps-control';
import { LocationFilled, LocationRegular } from "@fluentui/react-icons";
import { geocodeAddress, reverseGeocodeCoordinates, GeocodingResult } from "#root/utils/geocoding";
import { debounce } from 'lodash';
import { Location } from "#root/queries/locations";
import './styles.scss';

// Default coordinates for Los Angeles
const DEFAULT_COORDINATES = {
  latitude: 34.0522,
  longitude: -118.2437
};

interface LocationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (location: Location) => void;
  initialLocation?: Location;
  isLoading?: boolean;
}

export const LocationDialog: React.FC<LocationDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialLocation,
  isLoading = false
}) => {
  const [name, setName] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [coordinates, setCoordinates] = useState<GeocodingResult>(DEFAULT_COORDINATES);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<atlas.Map | null>(null);
  const isEditMode = !!initialLocation?.id;

  const mapOptions: IAzureMapOptions = {
    authOptions: {
      authType: AuthenticationType.subscriptionKey,
      subscriptionKey: process.env.REACT_APP_AZURE_MAPS
    },
    center: [coordinates.longitude, coordinates.latitude],
    zoom: 12,
    renderWorldCopies: false,
    showFeedbackLink: false,
    showLogo: false,
    style: 'grayscale_light'
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialLocation) {
        setName(initialLocation.name || '');
        setAddressInput(initialLocation.address || '');
        
        // Check if we have coordinates
        if (initialLocation.latitude && initialLocation.longitude) {
          setCoordinates({
            latitude: initialLocation.latitude!,
            longitude: initialLocation.longitude!
          });
        } else if (initialLocation.address) {
          // If we have an address but no coordinates, geocode the address
          const geocodeInitialAddress = async () => {
            try {
              setIsGeocodingLoading(true);
              const result = await geocodeAddress(initialLocation.address || '');
              if (result) {
                setCoordinates(result);
              } else {
                // Fallback to default if geocoding fails
                setCoordinates(DEFAULT_COORDINATES);
              }
            } catch (error) {
              console.error('Error geocoding initial address:', error);
              setCoordinates(DEFAULT_COORDINATES);
            } finally {
              setIsGeocodingLoading(false);
            }
          };
          
          geocodeInitialAddress();
        } else {
          // If no coordinates or address, use default
          setCoordinates(DEFAULT_COORDINATES);
        }
      } else {
        // New location - clear fields and try to get current location
        setName('');
        setAddressInput('');
        getCurrentLocation();
      }
    }
  }, [isOpen, initialLocation]);

  // Track if coordinates were changed by dragging the marker
  const isDraggingRef = useRef(false);

  // Reference to the marker
  const markerRef = useRef<atlas.HtmlMarker | null>(null);

  // Update map center when coordinates change (but not during marker drag)
  useEffect(() => {
    if (mapRef.current && mapReady) {
      // Only adjust camera if not dragging
      if (!isDraggingRef.current) {
        mapRef.current.setCamera({
          center: [coordinates.longitude, coordinates.latitude],
          zoom: 14
        });
      }

      // Always update marker position to match coordinates
      if (markerRef.current) {
        markerRef.current.setOptions({
          position: [coordinates.longitude, coordinates.latitude]
        });
      }
    }
    // Reset the dragging flag after the effect runs
    isDraggingRef.current = false;
  }, [coordinates, mapReady]);

  // Geocode address with debounce
  const geocodeAddressDebounced = useCallback(
    debounce(async (address: string) => {
      if (!address) return;

      setIsGeocodingLoading(true);
      try {
        const result = await geocodeAddress(address);
        if (result) {
          setCoordinates(result);
        }
      } catch (error) {
        console.error('Error geocoding address:', error);
      } finally {
        setIsGeocodingLoading(false);
      }
    }, 1000),
    []
  );

  // Handle address input change
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddressInput(value);

    if (value.trim()) {
      geocodeAddressDebounced(value);
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingCurrentLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        // Set coordinates immediately
        setCoordinates({ latitude, longitude });

        try {
          const result = await reverseGeocodeCoordinates(latitude, longitude);
          if (result?.address) {
            setAddressInput(result.address);
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
        }

        setIsGettingCurrentLocation(false);
      },
      (error) => {
        console.error('Error getting current location:', error);
        setIsGettingCurrentLocation(false);
        // Fall back to default location (Los Angeles)
        setCoordinates(DEFAULT_COORDINATES);
      }
    );
  };

  // Update marker position and get address for the location
  const updateMarkerPosition = async (latitude: number, longitude: number, marker?: atlas.HtmlMarker) => {
    // Set the dragging flag to prevent camera reset
    isDraggingRef.current = true;

    // Update coordinates immediately
    setCoordinates({ latitude, longitude });

    // Update marker position if provided
    if (marker) {
      marker.setOptions({
        position: [longitude, latitude]
      });
    }

    // Reverse geocode to get the address
    try {
      const result = await reverseGeocodeCoordinates(latitude, longitude);
      if (result?.address) {
        setAddressInput(result.address);
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    }
  };

  // Handle form submission
  const handleSave = () => {
    onSave({
      id: initialLocation?.id,
      incidentId: initialLocation?.incidentId,
      name,
      address: addressInput,
      latitude: coordinates.latitude,
      longitude: coordinates.longitude,
    });
  };

  return (
    <DialogBox
      open={isOpen}
      onOpenChange={onClose}
      title={isEditMode ? 'Edit Location' : 'New Location'}
      primaryButtonText="Save"
      onPrimaryButtonClick={handleSave}
      isPrimaryButtonLoading={isLoading}
      className="location-dialog"
    >
      <form className="location-form">
        <Field label="Name" required>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter location name"
          />
        </Field>

        <Field label="Address or Coordinates" required>
          <div className="address-input-container">
            <Input
              required
              value={addressInput}
              onChange={handleAddressChange}
              placeholder="Enter address or coordinates"
              contentAfter={
                isGeocodingLoading ? (
                  <Spinner size="tiny" />
                ) : (
                  <Tooltip content="Use current location" relationship="label">
                    <Button
                      appearance="transparent"
                      icon={<LocationRegular />}
                      onClick={getCurrentLocation}
                      disabled={isGettingCurrentLocation}
                      aria-label="Use current location"
                    />
                  </Tooltip>
                )
              }
            />
          </div>
        </Field>

        <div className="map-container">
          {isGettingCurrentLocation && (
            <div className="map-loading-overlay">
              <Spinner />
              <Text>Getting your location...</Text>
            </div>
          )}
          <AzureMapsProvider>
            <AzureMap
              options={mapOptions}
              events={{
                ready: (e: atlas.Map) => {
                  const mapInstance = (e as any).map || e;
                  mapRef.current = mapInstance;

                  // Create a marker and add it to the map
                  const marker = new atlas.HtmlMarker({
                    position: [coordinates.longitude, coordinates.latitude],
                    draggable: true,
                    htmlContent: '<div class="location-marker"><img src="/icons/marker.svg"/></div>'
                  });

                  // Add the marker to the map
                  mapInstance.markers.add(marker);
                  markerRef.current = marker;

                  // Add event listeners to the marker
                  mapInstance.events.remove('dragend', marker);
                  mapInstance.events.add('dragend', marker, async () => {
                    if (marker) {
                      const position = marker.getOptions().position;
                      if (position) {
                        const latitude = position[1];
                        const longitude = position[0];
                        await updateMarkerPosition(latitude, longitude);
                      }
                    }
                  });

                  // Add click event to the map to move the marker
                  mapInstance.events.add('click', async (e: atlas.MapMouseEvent) => {
                    // Get the clicked position
                    const position = e.position;
                    if (position && marker) {
                      const latitude = position[1];
                      const longitude = position[0];
                      await updateMarkerPosition(latitude, longitude, marker);
                    }
                  });

                  setMapReady(true);
                }
              }}
            />
          </AzureMapsProvider>
        </div>
      </form>
    </DialogBox>
  );
};

export default LocationDialog;